import { useMemo } from 'react';

export interface VirtualRowInput {
  /** Total items. */
  count: number;
  /** Item extent along the scroll axis (px). */
  itemSize: number;
  /** Gap between items (px). */
  gap: number;
  /** Visible extent of the viewport (px). */
  viewport: number;
  /** Index the focus is on; drives the scroll position. */
  focusedIndex: number;
  /** Extra items rendered on each side of the visible window (default 2). */
  overscan?: number;
}

export interface VirtualWindow {
  /** First index to render (inclusive). */
  start: number;
  /** Last index to render (exclusive). */
  end: number;
  /** Pixels to translate the track by (negative direction is applied by the caller). */
  offset: number;
  /** Total track extent. */
  total: number;
  /** Item stride (size + gap). */
  stride: number;
}

/**
 * Focus-driven windowing (docs/04 §2): the focused item is pinned to the leading edge
 * until the end of the list, where the track stops scrolling so the last page stays full.
 * Pure so it can be tested and reused by vertical lists.
 */
export function computeWindow({
  count,
  itemSize,
  gap,
  viewport,
  focusedIndex,
  overscan = 2,
}: VirtualRowInput): VirtualWindow {
  const stride = itemSize + gap;
  const total = count > 0 ? count * itemSize + (count - 1) * gap : 0;
  const maxOffset = Math.max(0, total - viewport);
  const idx = Math.max(0, Math.min(focusedIndex, Math.max(0, count - 1)));
  const offset = Math.min(idx * stride, maxOffset);
  const visible = Math.ceil(viewport / stride) + 1;
  const first = stride > 0 ? Math.floor(offset / stride) : 0;
  const start = Math.max(0, first - overscan);
  const end = Math.min(count, first + visible + overscan);
  return { start, end, offset, total, stride };
}

export function useVirtualRow(input: VirtualRowInput): VirtualWindow {
  const { count, itemSize, gap, viewport, focusedIndex, overscan } = input;
  return useMemo(
    () => computeWindow({ count, itemSize, gap, viewport, focusedIndex, overscan }),
    [count, itemSize, gap, viewport, focusedIndex, overscan],
  );
}

/** Position of an item on the track (before the track offset is applied). */
export function itemOffset(index: number, itemSize: number, gap: number): number {
  return index * (itemSize + gap);
}
